import { create } from 'zustand';
import { auth, db } from '../lib/firebase';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';

const useAuthStore = create((set, get) => ({ // Add 'get'
  user: null,
  profile: null,
  loading: true,
  error: null,
  localDeviceId: null,

  setLocalDeviceId: (id) => set({ localDeviceId: id }),

  initializeAuth: () => {
    set({ loading: true });
    let unsubProfile = null;

    // Listen for auth state changes
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }

      if (firebaseUser) {
        try {
          // Listen to the user document in Firestore in real-time
          const userRef = doc(db, 'users', firebaseUser.uid);

          unsubProfile = onSnapshot(userRef, (snap) => {
            if (snap.exists()) {
              const firestoreData = snap.data();
              // Standardize avatar property: custom avatarUrl takes precedence over Auth photoURL
              const finalizedPhotoURL = firestoreData.avatarUrl || firebaseUser.photoURL || firestoreData.photoURL;

              const mergedUser = {
                ...firebaseUser,
                ...firestoreData,
                photoURL: finalizedPhotoURL
              };

              const mergedProfile = {
                ...firestoreData,
                photoURL: finalizedPhotoURL
              };

              set({ user: mergedUser, profile: mergedProfile, loading: false });
            } else {
              // Create new user profile if it doesn't exist
              const userData = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName || `User ${firebaseUser.uid.slice(0, 5)}`,
                photoURL: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.email}&background=random`,
                createdAt: new Date().toISOString(),
                onboardingCompleted: false
              };
              setDoc(userRef, userData).catch(console.error);
              set({ user: { ...firebaseUser, ...userData }, profile: userData, loading: false });
            }
          });

        } catch (error) {
          console.error("Error setting up user listener:", error);
          set({ error: error.message, loading: false });
        }
      } else {
        set({ user: null, profile: null, loading: false, error: null });
      }
    });
  },

  signInWithGoogle: async () => {
    set({ loading: true, error: null });
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // onAuthStateChanged will handle the rest
    } catch (error) {
      console.error("Error signing in with Google:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  loginWithEmail: async (email, password) => {
    set({ loading: true, error: null });
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Error signing in with Email:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  registerWithEmail: async (email, password) => {
    set({ loading: true, error: null });
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Error registering with Email:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateProfile: async (data) => {
    set({ loading: true, error: null });
    try {
      const { user } = get();
      if (!user) throw new Error("No user logged in");

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });

      // Update local state with merged data
      // Note: partial update to 'user' object might be tricky if it's the firebase auth object
      // But we are storing additional data in Firestore.
      // We should probably re-fetch or merge manually.
      // For now, let's just assume the listener will pick it up? 
      // Actually, initializeAuth listener only runs on MOUNT or Auth State Change.
      // It does NOT listen to Firestore changes in real-time.
      // So we should manually update the local 'user' state if we want immediate feedback?
      // Or just reload.
      // Let's rely on the fact that we are updating the DB, and if we navigate, we might need to refresh context.
      // Actually, better to merge into state for responsiveness.
      // BUT `user` state currently holds the Firebase Auth User object primarily?
      // No, look at initializeAuth: `set({ user: firebaseUser ... })`
      // It DOES NOT merge Firestore data into the `user` state object in strict sense, 
      // except it checks if user exists.
      // Wait, look at line 34: `set({ user: firebaseUser ... })`.
      // It does NOT load firestore data into the `user` object in the store.
      // THIS IS A POTENTIAL ISSUE.
      // If `user.onboardingCompleted` is checked in PrivateRoute, it needs to be IN the user object.
      // FIX in this step: Update `initializeAuth` to merge Firestore data into `user` state.
    } catch (error) {
      console.error("Error updating profile:", error);
      set({ error: error.message, loading: false });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  logout: async () => {
    set({ loading: true });
    try {
      await signOut(auth);
      // onAuthStateChanged will handle state update
    } catch (error) {
      set({ error: error.message, loading: false });
    }
  },

  generatePairingCode: async () => {
    try {
      const { user } = get();
      if (!user) throw new Error("No user");

      // Generate "ABCD-1234"
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let code = "";
      for (let i = 0; i < 4; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
      code += "-";
      for (let i = 0; i < 4; i++) code += Math.floor(Math.random() * 10);

      // Save to Firestore
      await setDoc(doc(db, 'pairing_codes', code), {
        userId: user.uid,
        createdAt: serverTimestamp()
      });

      return code;
    } catch (error) {
      console.error("Error generating pairing code:", error);
      throw error;
    }
  },

  setTheme: async (newTheme) => {
    try {
      const { user } = get();
      if (!user) return;

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        theme: newTheme,
        updatedAt: new Date().toISOString()
      });
      // The onSnapshot listener in initializeAuth will automatically update local state
    } catch (error) {
      console.error("Error setting theme:", error);
    }
  },
}));

export default useAuthStore;
